/* eslint-disable react/prop-types */
"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import { isNativeApp } from "../../native/runtime";
import {
  MODAL_BACKDROP_CLASS,
  MODAL_PANEL_CLASS,
  appModalContentItemVariants,
  getModalPanelMaxHeightStyle,
  modalBackdropVariants,
  modalBottomSheetVariants,
  modalContentRootVariants,
  modalPanelVariants,
} from "./modalMotion";

export { appModalContentItemVariants } from "./modalMotion";

export const APP_MODAL_TITLE_CLASS =
  "min-w-0 py-1 text-center text-[clamp(1.45rem,6.2vw,1.82rem)] font-semibold leading-[1.22] tracking-tight text-white sm:text-[32px]";
export const APP_MODAL_DESCRIPTION_CLASS =
  "mb-5 text-[15px] font-medium leading-7 text-white/88";
export const APP_MODAL_BODY_TEXT_CLASS =
  "text-[14px] font-medium leading-6 text-white/78";
export const APP_MODAL_Z_INDEX_CLASS = "z-[13000]";

const KEYBOARD_OPEN_THRESHOLD_PX = 120;
const KEYBOARD_MOBILE_WIDTH_PX = 768;
const MOBILE_SHEET_TOP_INSET =
  "calc(var(--vg-safe-area-top, env(safe-area-inset-top, 0px)) + 8px)";

const APP_MODAL_BUTTON_VARIANTS = {
  primary:
    "bg-white text-[#19233A] shadow-[0_10px_28px_rgba(0,0,0,0.18)] disabled:bg-white/16 disabled:text-white/42 disabled:shadow-none",
  secondary:
    "border border-white/14 bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] disabled:text-white/42",
  danger:
    "bg-[#EF4458] text-white shadow-[0_10px_28px_rgba(239,68,88,0.24)] disabled:bg-white/16 disabled:text-white/42 disabled:shadow-none",
};

export function AppModalCloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path
        d="M5.5 5.5L16.5 16.5M16.5 5.5L5.5 16.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function AppModalIconButton({
  onClick,
  children,
  className = "",
  disabled = false,
  "aria-label": ariaLabel = "Modal action",
  ...rest
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      variants={appModalContentItemVariants}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:bg-white/14 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

export function AppModalContentItem({
  children,
  className = "",
  as: Component = motion.div,
  ...rest
}) {
  return (
    <Component variants={appModalContentItemVariants} className={className} {...rest}>
      {children}
    </Component>
  );
}

export function AppModalButton({
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = "primary",
  type = "button",
  className = "",
  ...rest
}) {
  const variantClassName = APP_MODAL_BUTTON_VARIANTS[variant] || APP_MODAL_BUTTON_VARIANTS.primary;
  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      variants={appModalContentItemVariants}
      className={`h-12 w-full rounded-full px-5 text-[16px] font-semibold leading-none transition active:scale-[0.985] disabled:cursor-not-allowed ${variantClassName} ${className}`}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

export function useKeyboardViewport(open, isDesktop) {
  const baselineViewportHeightRef = useRef(0);
  const nativeKeyboardHeightRef = useRef(0);
  const [keyboardViewport, setKeyboardViewport] = useState({
    open: false,
    top: 0,
    height: 0,
  });

  useEffect(() => {
    if (!open || isDesktop || typeof window === "undefined") {
      baselineViewportHeightRef.current = 0;
      nativeKeyboardHeightRef.current = 0;
      setKeyboardViewport({ open: false, top: 0, height: 0 });
      return undefined;
    }

    let rafId = null;
    const nativeApp = isNativeApp();

    const readViewport = () => {
      const vv = window.visualViewport;
      const layoutHeight = Math.max(
        0,
        Number(window.innerHeight || document.documentElement?.clientHeight || 0)
      );
      const visualHeight = Math.max(0, Number(vv?.height || layoutHeight));
      const visualWidth = Math.max(0, Number(vv?.width || window.innerWidth || 0));
      const visualTop = Math.max(0, Number(vv?.offsetTop || 0));

      baselineViewportHeightRef.current = Math.max(
        baselineViewportHeightRef.current,
        layoutHeight,
        visualHeight
      );

      const baselineHeight = baselineViewportHeightRef.current || visualHeight;
      const isMobileViewport = nativeApp || Math.min(
        visualWidth || window.innerWidth || 0,
        window.innerWidth || visualWidth || 0
      ) < KEYBOARD_MOBILE_WIDTH_PX;
      const nativeKeyboardHeight = nativeKeyboardHeightRef.current;
      const keyboardOpen =
        isMobileViewport &&
        (baselineHeight - visualHeight > KEYBOARD_OPEN_THRESHOLD_PX ||
          nativeKeyboardHeight > KEYBOARD_OPEN_THRESHOLD_PX);
      const usableHeight = nativeKeyboardHeight > KEYBOARD_OPEN_THRESHOLD_PX
        ? Math.min(visualHeight, baselineHeight - nativeKeyboardHeight)
        : visualHeight;

      setKeyboardViewport({
        open: keyboardOpen,
        top: keyboardOpen ? visualTop : 0,
        height: keyboardOpen ? Math.max(1, usableHeight) : 0,
      });
    };

    const scheduleViewportRead = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        readViewport();
      });
    };

    const resetBaselineAndRead = () => {
      baselineViewportHeightRef.current = 0;
      scheduleViewportRead();
    };

    readViewport();
    const timerOne = window.setTimeout(readViewport, 80);
    const timerTwo = window.setTimeout(readViewport, 240);
    let nativeListeners = [];
    let disposed = false;

    if (nativeApp) {
      import("@capacitor/keyboard").then(async ({ Keyboard }) => {
        const onShow = (event) => {
          nativeKeyboardHeightRef.current = Math.max(0, Number(event?.keyboardHeight || 0));
          scheduleViewportRead();
        };
        const onHide = () => {
          nativeKeyboardHeightRef.current = 0;
          scheduleViewportRead();
        };
        const handles = [];
        try {
          for (const [eventName, listener] of [
            ["keyboardWillShow", onShow],
            ["keyboardDidShow", onShow],
            ["keyboardWillHide", onHide],
            ["keyboardDidHide", onHide],
          ]) {
            handles.push(await Keyboard.addListener(eventName, listener));
          }
          if (disposed) {
            handles.forEach((handle) => handle?.remove?.());
          } else {
            nativeListeners = handles;
          }
        } catch {
          handles.forEach((handle) => handle?.remove?.());
        }
      }).catch(() => {});
    }

    window.addEventListener("resize", scheduleViewportRead, { passive: true });
    window.addEventListener("orientationchange", resetBaselineAndRead, { passive: true });
    window.visualViewport?.addEventListener?.("resize", scheduleViewportRead);
    window.visualViewport?.addEventListener?.("scroll", scheduleViewportRead);

    return () => {
      disposed = true;
      nativeListeners.forEach((handle) => handle?.remove?.());
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      window.clearTimeout(timerOne);
      window.clearTimeout(timerTwo);
      window.removeEventListener("resize", scheduleViewportRead);
      window.removeEventListener("orientationchange", resetBaselineAndRead);
      window.visualViewport?.removeEventListener?.("resize", scheduleViewportRead);
      window.visualViewport?.removeEventListener?.("scroll", scheduleViewportRead);
    };
  }, [isDesktop, open]);

  return keyboardViewport;
}

export default function AppModalShell({
  open,
  title,
  description = "",
  headerExtra = null,
  children,
  footer = null,
  leftAction = null,
  rightAction = null,
  onClose,
  closeAriaLabel = "Close",
  desktopMountNode = null,
  desktopMaxHeight = "min(82vh, 720px)",
  maxWidthClassName = "max-w-[520px]",
  headerClassName = "",
  titleClassName = "",
  descriptionClassName = "",
  panelClassName = "",
  panelStyle = null,
  bodyClassName = "",
  contentClassName = "",
  zIndexClassName = APP_MODAL_Z_INDEX_CLASS,
  overlayContent = null,
  layout = false,
  layoutTransition = { duration: 0.34, ease: [0.22, 1, 0.36, 1] },
  closeOnBackdropClick = true,
  rootStyle = null,
  viewportStyle = null,
  alignAboveKeyboard = false,
  forcePortalToBody = false,
  hideHeader = false,
  mobileSheet = false,
  mobileSheetTopInset = MOBILE_SHEET_TOP_INSET,
  preserveMobileSheetHeight = false,
  swipeToClose = false,
  onExitComplete,
}) {
  const isDesktop = useIsDesktop();
  const mobileSheetHeightRef = useRef(null);
  const sheetBodyRef = useRef(null);
  const sheetTouchStartRef = useRef(null);
  if (open && mobileSheet && preserveMobileSheetHeight && !isDesktop && mobileSheetHeightRef.current == null && typeof window !== "undefined") {
    mobileSheetHeightRef.current = Math.max(
      Number(window.innerHeight || 0),
      Number(window.visualViewport?.height || 0)
    );
  }
  const sandboxRoot =
    typeof window !== "undefined"
      ? window.__VG_MODAL_SANDBOX_ROOT || null
      : null;
  const portalRoot =
    typeof document !== "undefined"
      ? sandboxRoot ||
        (forcePortalToBody
          ? document.body
          : document.getElementById("modal-root") || document.body)
      : null;
  const mountNode = isDesktop ? desktopMountNode || portalRoot : portalRoot;
  const useScopedLayout =
    !!isDesktop &&
    !!mountNode &&
    typeof document !== "undefined" &&
    mountNode !== document.body;
  const keyboardViewport = useKeyboardViewport(open && !preserveMobileSheetHeight, isDesktop);
  const stableMobileSheet = mobileSheet && preserveMobileSheetHeight && !isDesktop && mobileSheetHeightRef.current;

  useBodyLock(Boolean(open) && !useScopedLayout, { preferSoftLock: true });

  useEffect(() => {
    if (!open || !onClose) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  const handleSheetTouchStart = (event) => {
    event.stopPropagation();
    sheetTouchStartRef.current = null;
    if (!swipeToClose || !mobileSheet || isDesktop || event.touches.length !== 1) return;
    if (sheetBodyRef.current?.scrollTop > 0) return;
    if (event.target.closest?.("input, textarea, select, button, [role='slider']")) return;
    sheetTouchStartRef.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  };

  const handleSheetTouchEnd = (event) => {
    const start = sheetTouchStartRef.current;
    sheetTouchStartRef.current = null;
    if (!start || !onClose || event.changedTouches.length !== 1) return;
    const deltaY = event.changedTouches[0].clientY - start.y;
    const deltaX = Math.abs(event.changedTouches[0].clientX - start.x);
    if (deltaY > 80 && deltaY > deltaX * 1.5 && !sheetBodyRef.current?.scrollTop) {
      onClose();
    }
  };

  if (!mountNode) return null;

  const resolvedPanelStyle = {
    ...getModalPanelMaxHeightStyle(isDesktop, desktopMaxHeight),
    ...(keyboardViewport.open
      ? {
          maxHeight: `calc(${keyboardViewport.height}px - max(env(safe-area-inset-top, 0px), 8px) - 18px)`,
        }
      : {}),
    ...(panelStyle || {}),
    ...(mobileSheet && !isDesktop
      ? {
          height: `calc(${stableMobileSheet ? `${stableMobileSheet}px` : "100dvh"} - ${mobileSheetTopInset})`,
          maxHeight: stableMobileSheet
            ? `calc(${stableMobileSheet}px - ${mobileSheetTopInset})`
            : keyboardViewport.open
            ? `calc(${keyboardViewport.height}px - ${mobileSheetTopInset})`
            : `calc(100dvh - ${mobileSheetTopInset})`,
        }
      : {}),
  };
  const resolvedViewportStyle = {
    ...(keyboardViewport.open
      ? {
          // Mobile Safari/WKWebView already positions fixed elements relative to
          // the visual viewport while the keyboard is open. Adding offsetTop
          // here applies the browser's focus pan a second time and pushes the
          // modal down underneath the keyboard.
          top: "0px",
          bottom: "auto",
          height: `${keyboardViewport.height}px`,
          alignItems: alignAboveKeyboard ? "flex-end" : "flex-start",
          padding: "max(env(safe-area-inset-top, 0px), 8px) 14px 10px",
        }
      : {}),
    ...(viewportStyle || {}),
    ...(mobileSheet && !isDesktop
      ? {
          alignItems: "flex-end",
          padding: `${mobileSheetTopInset} 0 0`,
        }
      : {}),
  };
  const layoutTransitionClass = layout
    ? "transition-[max-width,min-height,max-height,width,height] duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
    : "";

  const modal = (
    <AnimatePresence onExitComplete={() => {
      mobileSheetHeightRef.current = null;
      onExitComplete?.();
    }}>
      {open ? (
        <motion.div
          className={`${useScopedLayout ? "absolute" : "fixed"} inset-0 ${zIndexClassName} pointer-events-auto`}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="presentation"
          style={stableMobileSheet
            ? { bottom: "auto", height: `${stableMobileSheet}px`, ...(rootStyle || {}) }
            : rootStyle || undefined}
        >
          {closeOnBackdropClick && onClose ? (
            <motion.button
              type="button"
              className={`absolute inset-0 cursor-default ${MODAL_BACKDROP_CLASS}`}
              variants={modalBackdropVariants}
              onClick={onClose}
              aria-label={closeAriaLabel}
            />
          ) : (
            <motion.div
              className={`absolute inset-0 ${MODAL_BACKDROP_CLASS}`}
              variants={modalBackdropVariants}
              aria-hidden="true"
            />
          )}

          <div
            data-testid="app-modal-viewport"
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden p-4"
            style={
              Object.keys(resolvedViewportStyle).length
                ? resolvedViewportStyle
                : undefined
            }
          >
            <motion.section
              layout={layout}
              className={`pointer-events-auto relative z-10 flex w-full ${maxWidthClassName} origin-center flex-col overflow-hidden rounded-br-[30px] rounded-tl-[30px] rounded-bl-[10px] rounded-tr-[10px] ${layoutTransitionClass} ${MODAL_PANEL_CLASS} ${panelClassName}`}
              style={resolvedPanelStyle}
              variants={mobileSheet && !isDesktop ? modalBottomSheetVariants : modalPanelVariants}
              transition={layout ? { layout: layoutTransition } : undefined}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onTouchStart={handleSheetTouchStart}
              onTouchEnd={handleSheetTouchEnd}
              onTouchCancel={() => { sheetTouchStartRef.current = null; }}
              role="dialog"
              aria-modal="true"
              aria-label={typeof title === "string" ? title : undefined}
            >
              <motion.div variants={modalContentRootVariants} className={`flex min-h-0 flex-1 flex-col ${contentClassName}`}>
                {!hideHeader ? <header className={`relative shrink-0 px-5 pb-3 pt-5 ${headerClassName}`}>
                  <div className="relative min-h-[44px]">
                    {leftAction ? (
                      <motion.div variants={appModalContentItemVariants} className="absolute left-0 top-0 z-20 flex h-11 items-center justify-start">
                        {leftAction}
                      </motion.div>
                    ) : null}

                    <motion.div variants={appModalContentItemVariants} className="flex min-h-[44px] items-center justify-center px-12">
                      <h2 className={`${APP_MODAL_TITLE_CLASS} ${titleClassName}`}>
                        {title}
                      </h2>
                    </motion.div>

                    <div className="absolute right-0 top-0 z-20 flex items-start justify-end">
                      {rightAction || (
                        <AppModalIconButton onClick={onClose} aria-label={closeAriaLabel}>
                          <AppModalCloseIcon />
                        </AppModalIconButton>
                      )}
                    </div>
                  </div>
                  {headerExtra ? (
                    <motion.div variants={appModalContentItemVariants} className="flex justify-center">
                      {headerExtra}
                    </motion.div>
                  ) : null}
                </header> : null}

                <motion.div ref={sheetBodyRef} variants={modalContentRootVariants} className={`min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-1 ${bodyClassName}`}>
                  {description ? (
                    <motion.p variants={appModalContentItemVariants} className={`${APP_MODAL_DESCRIPTION_CLASS} ${descriptionClassName}`}>
                      {description}
                    </motion.p>
                  ) : null}
                  {children}
                </motion.div>

                {footer ? (
                  <motion.footer variants={appModalContentItemVariants} className="shrink-0 px-5 pb-5 pt-0">
                    {footer}
                  </motion.footer>
                ) : null}

                {overlayContent ? (
                  <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    {overlayContent}
                  </div>
                ) : null}
              </motion.div>
            </motion.section>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return createPortal(modal, mountNode);
}
